import {
  createProjectId,
  inferProjectNameFromFolder,
  normalizeSourceScanInterval,
} from "./utils.js";

export function normalizeSourceItem(item) {
  if (!item || typeof item !== "object") return null;
  const path = String(item.path || "").trim();
  if (!path) return null;
  return {
    path,
    name: String(item.name || inferProjectNameFromFolder(path) || path.split(/[\\/]/).pop() || "").trim(),
    selected: item.selected === true,
    checked: item.checked === true,
    importStatus: String(item.importStatus || "pending").trim() || "pending",
    processStatus: String(item.processStatus || "pending").trim() || "pending",
    activityProgress: Number.isFinite(Number(item.activityProgress)) ? Math.max(0, Math.min(96, Number(item.activityProgress))) : 0,
    sizeBytes: Number.isFinite(Number(item.sizeBytes)) ? Math.max(0, Number(item.sizeBytes)) : 0,
    modifiedAt: String(item.modifiedAt || "").trim(),
    seenAt: String(item.seenAt || "").trim(),
    lastObservedAt: String(item.lastObservedAt || "").trim(),
    lastChangedAt: String(item.lastChangedAt || "").trim(),
    readyState: String(item.readyState || "building").trim() || "building",
    filterProfile: String(item.filterProfile || "").trim(),
    filterStatus: String(item.filterStatus || "pending").trim() || "pending",
    gridAppendStatus: String(item.gridAppendStatus || "pending").trim() || "pending",
    svpNeedsReprocess: item.svpNeedsReprocess === true,
    svpAssignedProfile: String(item.svpAssignedProfile || "").trim(),
  };
}

export function normalizeGridItem(item) {
  if (!item || typeof item !== "object") return null;
  const name = String(item.name || item.gridName || "").trim();
  if (!name) return null;
  return {
    name,
    selected: item.selected === true,
  };
}

export function normalizeProjectRecord(project) {
  if (!project || typeof project !== "object") return null;
  const folder = String(project.folder || project.projectFolder || "").trim();
  if (!folder) return null;
  return {
    id: String(project.id || createProjectId()),
    name: String(project.name || inferProjectNameFromFolder(folder) || "Untitled Project").trim(),
    host: String(project.host || project.machine || "localhost").trim() || "localhost",
    folder,
    mode: String(project.mode || "existing").trim() || "existing",
    missingOnHost: project.missingOnHost === true,
    crsMode: String(project.crsMode || "").trim() || (String(project.wktFile || "").trim() ? "wkt-file" : "epsg"),
    epsgCode: String(project.epsgCode || "").trim(),
    wktFile: String(project.wktFile || "").trim(),
    surveyType: String(project.surveyType || "").trim(),
    description: String(project.description || "").trim(),
    sourceFolder: String(project.sourceFolder || "").trim(),
    sbetFile: String(project.sbetFile || "").trim(),
    sbetRmsFile: String(project.sbetRmsFile || "").trim(),
    sbetGpsWeek: String(project.sbetGpsWeek || "").trim(),
    sbetCoordinateSystem: String(project.sbetCoordinateSystem || "").trim(),
    sbetPriority: project.sbetPriority !== false,
    monitorSourceFolder: project.monitorSourceFolder === true,
    autoImportNewSources: project.autoImportNewSources === true,
    autoProcessNewSources: project.autoProcessNewSources === true,
    autoFilterSources: project.autoFilterSources === true,
    selectedFilterProfile: String(project.selectedFilterProfile || "").trim(),
    sourceScanIntervalSec: normalizeSourceScanInterval(project.sourceScanIntervalSec),
    sourceItems: Array.isArray(project.sourceItems) ? project.sourceItems.map(normalizeSourceItem).filter(Boolean) : [],
    gridItems: Array.isArray(project.gridItems) ? project.gridItems.map(normalizeGridItem).filter(Boolean) : [],
    gridSourceMemberships: project.gridSourceMemberships && typeof project.gridSourceMemberships === "object" ? project.gridSourceMemberships : {},
    tracklineStyle: project.tracklineStyle && typeof project.tracklineStyle === "object"
      ? {
          color: String(project.tracklineStyle.color || "#2dd4bf").trim() || "#2dd4bf",
          weight: Math.max(1, Math.min(5, Number(project.tracklineStyle.weight) || 3)),
          lineType: String(project.tracklineStyle.lineType || "solid").trim() || "solid",
          colorMode: String(project.tracklineStyle.colorMode || "solid").trim() || "solid",
        }
      : { color: "#2dd4bf", weight: 3, lineType: "solid", colorMode: "solid" },
    selectedGridName: String(project.selectedGridName || "").trim(),
    autoAppendProcessedToGrid: project.autoAppendProcessedToGrid === true,
    autoExportGeotiffOnGridAppend: project.autoExportGeotiffOnGridAppend !== false,
    lastGridAppendSignature: String(project.lastGridAppendSignature || "").trim(),
    svpAssignmentStrategy: String(project.svpAssignmentStrategy || "nearest-time").trim() || "nearest-time",
    svpMaxTimeHours: String(project.svpMaxTimeHours ?? "").trim(),
    svpMaxDistanceNm: String(project.svpMaxDistanceNm ?? "").trim(),
    addedAt: String(project.addedAt || new Date().toISOString()),
  };
}

export function getActiveProject(state) {
  const projects = Array.isArray(state.projects) ? state.projects : [];
  return projects.find((project) => project.id === state.selectedProjectId) || projects[0] || null;
}

export function getActiveProjectFolder(state) {
  return String(getActiveProject(state)?.folder || state.projectFolder || "").trim();
}

export function getActiveGridName(state) {
  const activeProject = getActiveProject(state);
  return String(activeProject?.selectedGridName || state.selectedGridName || state.gridName || "").trim();
}

export function getSelectedSourceExportNames(state) {
  const items = Array.isArray(getActiveProject(state)?.sourceItems) ? getActiveProject(state).sourceItems : [];
  return items
    .filter((item) => item?.selected)
    .map((item) => String(item.name || "").trim())
    .filter(Boolean);
}
